import { createContext, useContext, useState } from 'react';

const translations = {
  en: {
    // Navigation
    home: 'Home',
    venue: 'Venue',
    schedule: 'Schedule',
    accommodations: 'Where to Stay',
    gallery: 'Gallery',
    explore: 'Where to Visit',
    faq: 'FAQ',
    rsvp: 'RSVP',
    
    // Home Page
    weddingCelebration: 'Wedding Celebration',
    viewSchedule: 'View Schedule',
    
    // Schedule Page
    scheduleTitle: 'Schedule',
    weddingCelebrationSubtitle: 'Wedding Celebration',
    fridaySubtitle: 'The Day Before',
    saturdaySubtitle: 'Wedding Celebration',
    sundaySubtitle: 'The Day After',
    friday: 'Friday, September 4th, 2026',
    saturday: 'Saturday, September 5th, 2026',
    sunday: 'Sunday, September 6th, 2026',
    welcomeSoiree: 'Welcome Soirée',
    welcomeSoireeDesc: 'The warm-up round. Bring a dish, we\'ll bring the wine. Let\'s get this party started.',
    guestsArrival: 'Guest Arrival',
    ceremony: 'Ceremony',
    ceremonyDesc: 'The part where we say "I do" — you won\'t want to miss it.',
    cocktailHour: 'Golden Hour Cocktail',
    cocktailHourDesc: 'Champagne on the terrace while the sun sets. Peak vibes.',
    cocktailSubtitle: 'Cheers & Chandeliers',
    dinner: 'Dinner & Dessert',
    dinnerSubtitle: 'Bon Appétit',
    dinnerDesc: 'Five courses, local wines, and zero calorie counting.',
    receptionParty: 'Reception Party',
    receptionPartyDesc: 'The DJ is ready. The dance floor is waiting. Show us what you\'ve got.',
    soiree: 'Dance \'til Dawn',
    soireeDesc: 'The DJ is ready. The dance floor is waiting. Show us what you\'ve got.',
    farewellBrunch: 'Brunch',
    farewellBrunchSubtitle: 'The Morning After',
    farewellBrunchDesc: 'Croissants, coffee, and stories from the night before. Recovery mode: ON.',

    // Schedule Times
    timeWelcome: '6:00 PM - 9:30 PM',
    timeArrival: '2:30 PM - 3:00 PM',
    timeCeremony: '3:00 PM - 4:00 PM',
    timeCocktail: '4:30 PM - 7:30 PM',
    timeDinner: '7:30 PM - 10:30 PM',
    timeDance: '10:30 PM',
    timeDanceSuffix: 'All night long',
    timeBrunch: '11:00 AM - 2:00 PM',
    scheduleLocationNote: 'All events take place at Château des Joncherets',
    scheduleArrivalNote: 'There is a short walk from the château entrance to the ceremony location, so please arrive on time to ensure a timely start to the ceremony.',
    scheduleShuttleNote: 'A free shuttle will run from the Château on the night of Saturday, September 5th, from midnight until 4 AM. It will drop guests off at their accommodations in Dreux, or anywhere else within a 20-minute drive. The shuttle is for guests who signed up in advance. If you did not fill in the form but would still like a seat, please let us know as soon as you can.',
    chateauTerrace: 'Château Terrace',
    grandBallroom: 'Grand Ballroom',
    chateauGrounds: 'Château Grounds',
    
    // Venue Page
    venueTitle: 'The Venue',
    venueDescription: 'Château des Joncherets sits along the Avre river in the Normandy countryside, surrounded by moats, canals, and rolling green hills.',
    venueHistory: 'The château was built in 1620 under Louis XIII, designed by André Le Nôtre — the architect behind the gardens at Versailles. It has endured Viking-era conflicts, the French Revolution, and two World Wars. Over 400 years later, it has been carefully restored and remains one of the most distinctive estates in the Avre Valley.',
    address: 'Address',
    viewOnGoogleMaps: 'View on Google Maps',
    venueWebsite: 'Venue Website',
    
    // Accommodations Page
    accommodationsTitle: 'Where to Stay',
    accommodationsDescription: 'We\'ve gathered a selection of nearby places to stay, but please note that this list of accommodations is not exhaustive. We encourage you to search yourselves for options that suit you best! Accommodations are listed by distance from the Château.',
    hotelsTitle: 'Hotels',
    chateauxTitle: 'Châteaux / Domaines',
    onSite: 'On-site',
    minDrive: 'min drive',
    visitWebsite: 'Visit Website',
    viewOnAirbnb: 'View on Airbnb',
    viewOn: 'View on',
    bookNow: 'Book',
    fromChateau: 'from the Château',
    guests: 'guests',
    bedrooms: 'bedrooms',
    typeHotel: 'Hotel',
    typeRental: 'Rental',
    typeAirbnb: 'Airbnb',
    typeGites: 'Gîtes de France',
    forTwoNights: 'for 2 nights',
    nightsMin: 'nights min',
    perNight: 'night',
    walkingDistance: 'Walking distance',
    walk: 'walk',
    drive: 'drive',
    contactIfInterested: 'Great option for large groups. Contact us!',
    fullyBooked: 'Fully booked',
    accommodationsMapNote: 'Click a card or pin to highlight it on the map.',
    mapUnavailable: 'Map unavailable — add REACT_APP_GOOGLE_MAPS_API_KEY to enable.',
    chateauAccomDesc: 'Stay at the wedding venue itself. Limited rooms available for immediate family.',
    hotelSpaDesc: 'A charming 4-star hotel with spa services, just 10 minutes from the venue.',
    petitManoirDesc: 'Cozy bed and breakfast with authentic French countryside charm.',
    grandCerfDesc: 'Traditional French hotel with on-site restaurant in the heart of Nonancourt, just 6 km from the venue.',
    montignyDesc: '18th-century château on 15 hectares along the Avre river, classified as a historic monument. Sleeps up to 38 — ideal for large groups that want to stay together. 75 minutes from Paris.',
    primardDesc: '5-star Michelin-recognized estate in Guainville with 39 rooms, spa, pool, and free bikes. Set on gorgeous grounds about an hour from Paris.',
    verneuilLaDouceDesc: 'Boutique art hotel and spa in Verneuil-sur-Avre with beautifully designed suites (35-50 m²), a restaurant, and a seasonal pool. A proper retreat.',
    boulayMorinDesc: '4-star château hotel near Évreux, built in 1695 by Louis XIV. 41 hectares of forest and gardens, heated pool, tennis court, and just 10 personalized rooms.',
    hotelNormandieDesc: 'Hotel in the center of Dreux, about 16 km from the Château. Convenient access to shops and the train station.',
    hotelSaumonDesc: 'Charming hotel with restaurant near the railway station in Verneuil-sur-Avre. Converted from a former post office, offering à la carte dining and buffet breakfast. Rated 8.5/10.',
    aubergeValleeDesc: 'Cozy country hotel in Vernouillet with a warm bar and café atmosphere. Highly rated at 10/10 on Booking.com. About 45 minutes from Paris.',
    ibisDreuxDesc: 'Modern hotel steps from Dreux train station. Clean, comfortable rooms with easy access to the town center. Rated 9.8/10.',
    hotelDesLysDesc: 'Well-rated hotel in Dreux offering comfortable rooms at great value. Convenient for guests arriving by train. Rated 8.8/10.',
    airbnb1Name: 'Oasis of Calm',
    airbnb1Desc: 'Beautifully renovated riverside house in Nonancourt. 4 bedrooms, garden with barbecue, peaceful setting just minutes from the venue. Guest favorite with perfect 5.0 rating.',
    airbnb2Name: 'House with Pool',
    airbnb2Desc: 'Spacious country house in Saint-Lubin-des-Joncherets with private pool, 5 bedrooms, and large garden. Perfect for groups — just minutes from the Château.',
    airbnb3Name: 'Cozy Cottage with Garden',
    airbnb3Desc: 'Charming semi-detached cottage in Tréon with a large garden. Intimate and cozy, ideal for couples or a small group. Guest favorite, 5.0 rated.',
    airbnb4Name: 'Charming House to Get Away',
    airbnb4Desc: 'Spacious countryside retreat in La Madeleine-de-Nonancourt, ideal for up to 3 couples. Pétanque court, outdoor dining, barbecue, and a cozy lounge for cooler evenings. Guest favorite, 4.97 rated.',
    airbnb5Name: 'La Maison de Fessanvilliers',
    airbnb5Desc: 'A beautifully renovated house with character in Fessanvilliers-Mattanvilliers. Heated pool, private garden, bicycles, and ping-pong. Homely charm at the crossroads of Beauce, Perche, and Normandy. Guest favorite, 4.97 rated.',
    airbnb6Name: 'Moulin Rouge Bed & Breakfast',
    airbnb6Desc: 'A former flour mill on the riverbank in Vernouillet with 3 hectares of green space. Cozy gîte for 2-3 guests with equipped kitchen, terrace, and barbecue. Peaceful riverside setting, 45 min from Paris. Guest favorite, 4.91 rated.',
    airbnb7Name: 'Charming Farm Guest Apartment',
    airbnb7Desc: 'A cozy 2-bedroom apartment on a working sheep farm in Saint-Germain-sur-Avre. Private entrance, equipped kitchen, garden, and BBQ under the cherry trees. Pets welcome. A unique countryside experience.',
    
    // Gallery Page
    galleryTitle: 'Gallery',
    lightboxLabel: 'Image lightbox',
    lightboxClose: 'Close',
    lightboxPrevious: 'Previous image',
    lightboxNext: 'Next image',

    // RSVP Page
    rsvpTitle: 'RSVP',
    rsvpDescription: 'We\'d love to celebrate with you! Let us know if you can join us for the weekend.',
    rsvpOnePerPerson: 'Please fill in one form per person. Each guest should submit their own RSVP.',
    rsvpOnZola: 'RSVP on Zola',
    viewRegistry: 'Registry',
    registryTitle: 'Registry',
    registryIntro: 'Your presence is the greatest gift, but if you\'d like to contribute, we\'ve set up two registries. Pick the one that matches your currency.',
    registryZolaLabel: 'Zola',
    registryZolaSubLabel: 'English / USD',
    registryMmmLabel: 'MilleMercisMariage',
    registryMmmSubLabel: 'French / EUR',
    registryOpenLink: 'Open registry',
    rsvpRequiredNote: '* Required field',
    rsvpName: 'Your Name',
    rsvpNamePlaceholder: 'First and Last Name',
    rsvpEmail: 'Email Address',
    rsvpEmailPlaceholder: 'your@email.com',
    rsvpEmailInvalid: 'Please enter a valid email address',
    rsvpDayRequired: 'Please select an option',
    rsvpFriday: 'Will you join us for our Soirée de Bienvenue on Friday, September 4th (18:00 - 21:30)?',
    rsvpSaturday: 'Will you join us for the Celebration – Cocktail – Dinner – Evening Party on Saturday, September 5th (3:00 PM → All night long)?',
    rsvpSunday: 'Will you join us for Brunch on Sunday, September 6th (11:00 AM - 2:00 PM)?',
    rsvpYes: 'Yes',
    rsvpNo: 'No',
    rsvpNotSure: 'Not sure yet',
    rsvpSubmit: 'Send',
    rsvpSubmitting: 'Sending...',
    rsvpThankYou: 'Thank You!',
    rsvpThankYouDesc: 'We\'ve got your response. See you in September!',
    rsvpSubmitAnother: 'Submit another response',
    rsvpWhatsapp: 'WhatsApp Number',
    rsvpWhatsappPlaceholder: '+1 (555) 123-4567',
    rsvpWhatsappInvalid: 'Please enter a valid phone number',
    rsvpEmailAlt: 'Alternate Email Address',
    rsvpFridayDesc: 'Let us know what kind of dish(es) you\'ll be bringing',
    rsvpDietaryRestrictions: 'Any dietary restrictions?',
    rsvpDietaryRestrictionsPlaceholder: 'Allergies, intolerances, vegetarian, vegan... or "None"',
    rsvpDietaryRequired: 'Please let us know about any dietary restrictions (or write "None")',
    rsvpSpecialRequests: 'Any special requests? (Song requests, wishes, propositions, etc.)',
    rsvpSpecialRequestsPlaceholder: 'Let us know...',
    rsvpQuestions: 'Any questions for Adrienne & Thomas?',
    rsvpQuestionsPlaceholder: 'Ask us anything...',
    rsvpGoogleNote: 'Your answers will be pre-filled on the Google Form — just sign in and hit Submit!',
    rsvpConfirmed: 'Your RSVP has been received',
    rsvpModify: 'Update',
    rsvpCancel: 'Cancel',
    rsvpFridayShort: 'Friday — Soirée de Bienvenue',
    rsvpSaturdayShort: 'Saturday — Wedding Celebration',
    rsvpSundayShort: 'Sunday — Brunch',
    rsvpFetchError: 'Could not load your RSVP. Please try again later.',
    rsvpContactTitle: 'Your Contact Details',
    rsvpContactOnFile: 'We have your contact details — thank you!',
    rsvpContactUpdate: 'Update',
    rsvpPostalAddress: 'Postal Address',
    rsvpPostalAddressPlaceholder: 'Street, City, ZIP, Country',
    rsvpPostalAddressRequired: 'Please enter your postal address',
    rsvpContactSubmit: 'Save',
    rsvpContactSubmitting: 'Saving...',
    rsvpAttendanceTitle: 'Your Attendance',
    rsvpAttendanceConfirmed: 'We have your RSVP — thank you!',

    // Auth
    loginDate: 'September 5th, 2026',
    loginName: 'Your Full Name',
    loginNamePlaceholder: 'First Name Last Name',
    loginPassword: 'Password',
    loginRememberName: 'Remember me',
    loginSubmit: 'Enter',
    loginSubmitHint: 'Please fill in your name and password to continue.',
    loginLoading: 'Signing in...',
    loginShowPassword: 'Show password',
    loginHidePassword: 'Hide password',
    loginErrorPassword: 'Incorrect password. Please try again.',
    loginErrorName: 'Name not found in guest list. Please check your spelling or contact Adrienne & Thomas.',
    loginErrorGeneric: 'Something went wrong. Please try again.',
    sessionExpired: 'Your session has expired. Please log in again.',
    logout: 'Log out',
    toggleMenu: 'Toggle menu',
    mobileMenuLabel: 'Navigation menu',

    // Footer
    madeWith: 'Made with',
    footerDate: 'September 5th, 2026 • Château des Joncherets',

    // FAQ Page
    faqTitle: 'FAQ',
    faqComingSoon: 'Coming Soon!',

    // Where to Visit Page
    whereToVisitTitle: 'Where to Visit',
    whereToVisitDesc: 'If you\'re arriving early or staying after the weekend, there is plenty to see and do around the château. The Avre Valley offers rich history, wonderful food, and beautiful countryside.',
    groundsActivitiesTitle: 'Grounds & Activities',
    dayTripsTitle: 'Day Trips',
    exploreOutdoorsTitle: 'Explore the Outdoors',
    exploreOutdoorsDesc: 'The domaine is nestled on the Avre river, which provides fresh water to the park\'s moats, canals, and reflection pool. Guests can hop in and out of their canoe right from the château moat! Enjoy fishing, kayaking, and canoeing.',
    bikeMontTitle: 'Bike to Mont-Saint-Michel',
    bikeMontDesc: 'Château des Joncherets is a 50-minute drive from a 280-mile bike trail that takes adventure seekers from Paris all the way to Mont-Saint-Michel. This magical UNESCO World Heritage site is famous for its quicksand, monastery, and crêperies.',
    natureWalksTitle: 'Nature Walks',
    natureWalksDesc: 'Take in the surrounding countryside with a 6-mile hiking path around the Avre valley and river, starting just a few meters from the castle. The trek takes you through medieval towns, historical architecture, rivers, and quaint bridges and cafés.',
    tennisTitle: 'Tennis & Squash',
    tennisDesc: 'A 14-minute walk will bring you to the Tennis & Squash Club with multiple courts and lighting, where visitors can participate in drop-in games. There are also showers and changing rooms.',
    linkFishing: 'Fishing',
    linkGoKarting: 'Go-Karting',
    linkKayaking: 'Kayaking & Canoeing',
    linkVeloscenic: 'Veloscenic Bike Route',
    linkTrailMap: 'Trail Map',
    evreuxTitle: 'Évreux',
    evreuxDesc: 'Évreux features a beautiful church, a historic center, and high-end dining options. Walk along the tree-lined canals and stop at a restaurant to taste the local specialty — roasted camembert!',
    evreux30min: '30-minute drive',
    versaillesTitle: 'Versailles',
    versaillesDesc: 'Versailles is one of the most visited cities in the world thanks to its miles of preserved forest, royal château, and history.',
    versailles60min: '60-min drive or 45-min train',
    verneuiltTitle: 'Verneuil-sur-Avre',
    verneuilDesc: 'Medieval town with a castle, a church, and a tower. Cobblestone streets, half-timbered houses, and plenty of restaurants and shops to wander through.',
    verneuil20min: '20-minute drive',
  },
  fr: {
    // Navigation
    home: 'Accueil',
    venue: 'Lieu',
    schedule: 'Programme',
    accommodations: 'Hébergements',
    gallery: 'Galerie',
    explore: 'À Découvrir',
    faq: 'FAQ',
    rsvp: 'RSVP',
    
    // Home Page
    weddingCelebration: 'Célébration de Mariage',
    viewSchedule: 'Voir le Programme',
    
    // Schedule Page
    scheduleTitle: 'Programme',
    weddingCelebrationSubtitle: 'Célébration de Mariage',
    fridaySubtitle: 'La Veille',
    saturdaySubtitle: 'Célébration de Mariage',
    sundaySubtitle: 'Le Lendemain',
    friday: 'Vendredi 4 Septembre 2026',
    saturday: 'Samedi 5 Septembre 2026',
    sunday: 'Dimanche 6 Septembre 2026',
    welcomeSoiree: 'Soirée de Bienvenue',
    welcomeSoireeDesc: 'L\'échauffement. Apportez un plat, on apporte le vin. Que la fête commence.',
    guestsArrival: 'Arrivée des Invités',
    ceremony: 'Cérémonie',
    ceremonyDesc: 'Le moment où on dit "oui" — vous ne voudrez pas rater ça.',
    cocktailHour: 'Golden Hour Cocktail',
    cocktailHourDesc: 'Champagne sur la terrasse au coucher du soleil. L\'ambiance sera au rendez-vous.',
    cocktailSubtitle: 'Cheers & Chandeliers',
    dinner: 'Dîner & Dessert',
    dinnerSubtitle: 'Bon Appétit',
    dinnerDesc: 'Cinq plats, vins locaux, et zéro comptage de calories.',
    receptionParty: 'Soirée Dansante',
    receptionPartyDesc: 'Le DJ est prêt. La piste vous attend. Montrez-nous ce que vous savez faire.',
    soiree: 'On Danse Jusqu\'à l\'Aube',
    soireeDesc: 'Le DJ est prêt. La piste vous attend. Montrez-nous ce que vous savez faire.',
    farewellBrunch: 'Brunch',
    farewellBrunchSubtitle: 'Le Lendemain',
    farewellBrunchDesc: 'Croissants, café, et récits de la veille. Mode récupération : activé.',

    // Schedule Times
    timeWelcome: '18h00 - 21h30',
    timeArrival: '14h30 - 15h00',
    timeCeremony: '15h00 - 16h00',
    timeCocktail: '16h30 - 19h30',
    timeDinner: '19h30 - 22h30',
    timeDance: '22h30',
    timeDanceSuffix: 'Bout de la nuit',
    timeBrunch: '11h00 - 14h00',
    scheduleLocationNote: 'Tous les événements se déroulent au Château des Joncherets',
    scheduleArrivalNote: 'Une courte marche vous mènera de l\'entrée du château au lieu de la cérémonie : merci d\'être ponctuels pour que nous puissions commencer à l\'heure.',
    scheduleShuttleNote: 'Une navette gratuite partira du Château dans la nuit du samedi 5 au dimanche 6 septembre, de minuit à 4h du matin. Elle déposera les invités à leur hébergement, à Dreux ou partout dans un rayon de 20 minutes. La navette est réservée aux invités inscrits à l\'avance. Si vous n\'avez pas rempli le formulaire mais souhaitez une place, faites-le nous savoir dès que possible.',
    chateauTerrace: 'Terrasse du Château',
    grandBallroom: 'Grande Salle de Bal',
    chateauGrounds: 'Parc du Château',
    
    // Venue Page
    venueTitle: 'Le Lieu',
    venueDescription: 'Le Château des Joncherets se trouve au bord de la rivière Avre, en pleine campagne normande, entouré de douves, de canaux et de collines verdoyantes.',
    venueHistory: 'Le château a été construit en 1620 sous Louis XIII, conçu par André Le Nôtre — l\'architecte des jardins de Versailles. Il a traversé les conflits de l\'époque viking, la Révolution française et deux guerres mondiales. Plus de 400 ans après, soigneusement restauré, il reste l\'un des domaines les plus remarquables de la vallée de l\'Avre.',
    address: 'Adresse',
    viewOnGoogleMaps: 'Voir sur Google Maps',
    venueWebsite: 'Site du Château',
    
    // Accommodations Page
    accommodationsTitle: 'Hébergements',
    accommodationsDescription: 'Nous avons rassemblé une sélection d\'hébergements à proximité, mais cette liste n\'est pas exhaustive. Nous vous encourageons à rechercher par vous-mêmes les options qui vous conviennent le mieux ! Les hébergements sont classés par distance depuis le Château.',
    hotelsTitle: 'Hôtels',
    chateauxTitle: 'Châteaux / Domaines',
    onSite: 'Sur place',
    minDrive: 'min en voiture',
    visitWebsite: 'Visiter le Site',
    viewOnAirbnb: 'Voir sur Airbnb',
    viewOn: 'Voir sur',
    bookNow: 'Réserver',
    fromChateau: 'du Château',
    typeHotel: 'Hôtel',
    typeRental: 'Location',
    typeAirbnb: 'Airbnb',
    typeGites: 'Gîtes de France',
    forTwoNights: 'pour 2 nuits',
    nightsMin: 'nuits minimum',
    perNight: 'nuit',
    walkingDistance: 'À pied',
    walk: 'à pied',
    drive: 'en voiture',
    contactIfInterested: 'Idéal pour les grands groupes. Contactez-nous !',
    fullyBooked: 'Complet',
    accommodationsMapNote: 'Cliquez sur une carte ou une épingle pour la mettre en avant.',
    mapUnavailable: 'Carte indisponible — ajoutez REACT_APP_GOOGLE_MAPS_API_KEY pour l\'activer.',
    guests: 'invités',
    bedrooms: 'chambres',
    chateauAccomDesc: 'Séjournez au lieu du mariage. Chambres limitées disponibles pour la famille proche.',
    hotelSpaDesc: 'Un charmant hôtel 4 étoiles avec services spa, à seulement 10 minutes du lieu.',
    petitManoirDesc: 'Chambre d\'hôtes cosy avec un charme authentique de la campagne française.',
    grandCerfDesc: 'Hôtel traditionnel français avec restaurant sur place au cœur de Nonancourt, à seulement 6 km du lieu.',
    montignyDesc: 'Château du XVIIIe siècle sur 15 hectares au bord de la rivière Avre, classé monument historique. Peut accueillir jusqu\'à 38 personnes — idéal pour les grands groupes souhaitant loger ensemble. À 75 minutes de Paris.',
    primardDesc: 'Domaine 5 étoiles reconnu par le Guide Michelin à Guainville. 39 chambres, spa, piscine et vélos gratuits. Cadre superbe à environ 1h de Paris.',
    verneuilLaDouceDesc: 'Hôtel-boutique d\'art et spa à Verneuil-sur-Avre avec des suites soignées (35-50 m²), un restaurant et une piscine saisonnière. Un vrai lieu de retraite.',
    boulayMorinDesc: 'Château-hôtel 4 étoiles près d\'Évreux, construit en 1695 par Louis XIV. 41 hectares de forêt et jardins, piscine chauffée, court de tennis, et seulement 10 chambres personnalisées.',
    hotelNormandieDesc: 'Hôtel au centre de Dreux, à environ 16 km du Château. Accès pratique aux commerces et à la gare.',
    hotelSaumonDesc: 'Hôtel de charme avec restaurant près de la gare de Verneuil-sur-Avre. Ancienne poste reconvertie, proposant une cuisine à la carte et un petit-déjeuner buffet. Noté 8.5/10.',
    aubergeValleeDesc: 'Auberge de campagne chaleureuse à Vernouillet avec bar et café. Très bien notée 10/10 sur Booking.com. À environ 45 minutes de Paris.',
    ibisDreuxDesc: 'Hôtel moderne à deux pas de la gare de Dreux. Chambres propres et confortables avec accès facile au centre-ville. Noté 9.8/10.',
    hotelDesLysDesc: 'Hôtel bien noté à Dreux offrant des chambres confortables à bon rapport qualité-prix. Pratique pour les invités arrivant en train. Noté 8.8/10.',
    airbnb1Name: 'Oasis de Calme',
    airbnb1Desc: 'Maison de charme rénovée au bord de la rivière à Nonancourt. 4 chambres, jardin avec barbecue, cadre paisible à quelques minutes du lieu. Coup de cœur des voyageurs, note parfaite de 5.0.',
    airbnb2Name: 'Maison avec Piscine',
    airbnb2Desc: 'Spacieuse maison de campagne à Saint-Lubin-des-Joncherets avec piscine privée, 5 chambres et grand jardin. Idéale pour les groupes — à quelques minutes du Château.',
    airbnb3Name: 'Charmant Cottage avec Jardin',
    airbnb3Desc: 'Charmant cottage mitoyen à Tréon avec un grand jardin. Intime et cosy, idéal pour un couple ou un petit groupe. Coup de cœur des voyageurs, noté 5.0.',
    airbnb4Name: 'Maison de Charme pour S\'évader',
    airbnb4Desc: 'Spacieuse retraite de campagne à La Madeleine-de-Nonancourt, idéale pour 3 couples. Terrain de pétanque, repas en plein air, barbecue et un salon cosy pour les soirées fraîches. Coup de cœur des voyageurs, noté 4.97.',
    airbnb5Name: 'La Maison de Fessanvilliers',
    airbnb5Desc: 'Maison de caractère joliment rénovée à Fessanvilliers-Mattanvilliers. Piscine chauffée, jardin privé, vélos et ping-pong. Charme authentique au carrefour de la Beauce, du Perche et de la Normandie. Coup de cœur des voyageurs, noté 4.97.',
    airbnb6Name: 'Moulin Rouge Chambres d\'Hôtes',
    airbnb6Desc: 'Ancien moulin à farine au bord de la rivière à Vernouillet avec 3 hectares d\'espace vert. Gîte cosy pour 2-3 personnes avec cuisine équipée, terrasse et barbecue. Cadre paisible au fil de l\'eau, à 45 min de Paris. Coup de cœur des voyageurs, noté 4.91.',
    airbnb7Name: 'Appartement de Charme à la Ferme',
    airbnb7Desc: 'Appartement cosy de 2 chambres dans une ferme ovine à Saint-Germain-sur-Avre. Entrée privée, cuisine équipée, jardin et barbecue sous les cerisiers. Animaux bienvenus. Une expérience unique à la campagne.',
    
    // Gallery Page
    galleryTitle: 'Galerie',
    lightboxLabel: 'Visionneuse d\'images',
    lightboxClose: 'Fermer',
    lightboxPrevious: 'Image précédente',
    lightboxNext: 'Image suivante',

    // RSVP Page
    rsvpTitle: 'RSVP',
    rsvpDescription: 'On serait ravis de fêter ça avec vous ! Dites-nous si vous pouvez venir pour le week-end.',
    rsvpOnePerPerson: 'Merci de remplir un formulaire par personne. Chaque invité doit soumettre son propre RSVP.',
    rsvpOnZola: 'RSVP sur Zola',
    viewRegistry: 'Liste de Mariage',
    registryTitle: 'Liste de Mariage',
    registryIntro: 'Votre présence est notre plus beau cadeau, mais si vous souhaitez contribuer, nous avons mis en place deux listes. Choisissez celle qui correspond à votre monnaie.',
    registryZolaLabel: 'Zola',
    registryZolaSubLabel: 'Anglais / USD',
    registryMmmLabel: 'MilleMercisMariage',
    registryMmmSubLabel: 'Français / EUR',
    registryOpenLink: 'Ouvrir la liste',
    rsvpRequiredNote: '* Champ obligatoire',
    rsvpName: 'Votre Nom',
    rsvpNamePlaceholder: 'Prénom et Nom',
    rsvpEmail: 'Adresse Email',
    rsvpEmailPlaceholder: 'votre@email.com',
    rsvpEmailInvalid: 'Veuillez saisir une adresse email valide',
    rsvpDayRequired: 'Veuillez sélectionner une option',
    rsvpFriday: 'Serez-vous des nôtres pour la Soirée de Bienvenue le vendredi 4 septembre (18h00 - 21h30) ?',
    rsvpSaturday: 'Serez-vous des nôtres pour la Célébration – Cocktail – Dîner – Soirée le samedi 5 septembre (15h00 → Bout de la nuit) ?',
    rsvpSunday: 'Serez-vous des nôtres pour le Brunch le dimanche 6 septembre (11h00 - 14h00) ?',
    rsvpYes: 'Oui',
    rsvpNo: 'Non',
    rsvpNotSure: 'Pas encore sûr(e)',
    rsvpSubmit: 'Envoyer',
    rsvpSubmitting: 'Envoi en cours...',
    rsvpThankYou: 'Merci !',
    rsvpThankYouDesc: 'C\'est noté. À septembre !',
    rsvpSubmitAnother: 'Soumettre une autre réponse',
    rsvpWhatsapp: 'Numéro WhatsApp',
    rsvpWhatsappPlaceholder: '+33 6 12 34 56 78',
    rsvpWhatsappInvalid: 'Veuillez saisir un numéro de téléphone valide',
    rsvpEmailAlt: 'Adresse Email Alternative',
    rsvpFridayDesc: 'Dites-nous quel(s) plat(s) vous apporterez',
    rsvpDietaryRestrictions: 'Des restrictions alimentaires ?',
    rsvpDietaryRestrictionsPlaceholder: 'Allergies, intolérances, végétarien, vegan... ou "Aucune"',
    rsvpDietaryRequired: 'Veuillez indiquer vos restrictions alimentaires (ou écrire "Aucune")',
    rsvpSpecialRequests: 'Des demandes spéciales ? (Musique, souhaits, propositions, etc.)',
    rsvpSpecialRequestsPlaceholder: 'Dites-nous...',
    rsvpQuestions: 'Des questions pour Adrienne & Thomas ?',
    rsvpQuestionsPlaceholder: 'Posez vos questions...',
    rsvpGoogleNote: 'Vos réponses seront pré-remplies sur le Google Form — connectez-vous et cliquez sur Soumettre !',
    rsvpConfirmed: 'Votre RSVP a bien été reçu',
    rsvpModify: 'Mettre à jour',
    rsvpCancel: 'Annuler',
    rsvpFridayShort: 'Vendredi — Soirée de Bienvenue',
    rsvpSaturdayShort: 'Samedi — Célébration de Mariage',
    rsvpSundayShort: 'Dimanche — Brunch',
    rsvpFetchError: 'Impossible de charger votre RSVP. Veuillez réessayer plus tard.',
    rsvpContactTitle: 'Vos Coordonnées',
    rsvpContactOnFile: 'Nous avons vos coordonnées — merci !',
    rsvpContactUpdate: 'Mettre à jour',
    rsvpPostalAddress: 'Adresse Postale',
    rsvpPostalAddressPlaceholder: 'Rue, Ville, Code Postal, Pays',
    rsvpPostalAddressRequired: 'Veuillez saisir votre adresse postale',
    rsvpContactSubmit: 'Enregistrer',
    rsvpContactSubmitting: 'Enregistrement...',
    rsvpAttendanceTitle: 'Votre Présence',
    rsvpAttendanceConfirmed: 'Nous avons votre RSVP — merci !',

    // Auth
    loginDate: '5 septembre 2026',
    loginName: 'Votre Nom Complet',
    loginNamePlaceholder: 'Prénom Nom',
    loginPassword: 'Mot de passe',
    loginRememberName: 'Se souvenir de moi',
    loginSubmit: 'Entrer',
    loginSubmitHint: 'Veuillez renseigner votre nom et votre mot de passe pour continuer.',
    loginLoading: 'Connexion...',
    loginShowPassword: 'Afficher le mot de passe',
    loginHidePassword: 'Masquer le mot de passe',
    loginErrorPassword: 'Mot de passe incorrect. Veuillez réessayer.',
    loginErrorName: 'Nom introuvable dans la liste des invités. Vérifiez l\'orthographe ou contactez Adrienne & Thomas.',
    loginErrorGeneric: 'Une erreur est survenue. Veuillez réessayer.',
    sessionExpired: 'Votre session a expiré. Veuillez vous reconnecter.',
    logout: 'Déconnexion',
    toggleMenu: 'Ouvrir/fermer le menu',
    mobileMenuLabel: 'Menu de navigation',

    // Footer
    madeWith: 'Fait avec',
    footerDate: '5 septembre 2026 • Château des Joncherets',

    // FAQ Page
    faqTitle: 'FAQ',
    faqComingSoon: 'Bientôt disponible !',

    // Where to Visit Page
    whereToVisitTitle: 'À Découvrir',
    whereToVisitDesc: 'Si vous arrivez en avance ou restez après le week-end, il y a beaucoup à voir et à faire autour du Château. La vallée de l\'Avre offre une riche histoire, une cuisine remarquable et une campagne magnifique.',
    groundsActivitiesTitle: 'Domaine & Activités',
    dayTripsTitle: 'Excursions à la Journée',
    exploreOutdoorsTitle: 'En Plein Air',
    exploreOutdoorsDesc: 'Le domaine est niché au bord de la rivière Avre, qui alimente les douves, canaux et le bassin du parc. Les invités peuvent monter et descendre du canoë directement depuis les douves du Château ! Profitez de la pêche, du kayak et du canoë.',
    bikeMontTitle: 'Vélo jusqu\'au Mont-Saint-Michel',
    bikeMontDesc: 'Le Château des Joncherets est à 50 minutes en voiture d\'une piste cyclable de 450 km qui mène les aventuriers de Paris jusqu\'au Mont-Saint-Michel. Ce site magique classé au patrimoine mondial de l\'UNESCO est célèbre pour ses sables mouvants, son monastère et ses crêperies.',
    natureWalksTitle: 'Randonnées Nature',
    natureWalksDesc: 'Profitez de la campagne environnante avec un sentier de randonnée de 10 km autour de la vallée et de la rivière Avre, commençant à quelques mètres du Château. Le parcours traverse des villes médiévales, une architecture historique, des rivières et de charmants ponts et cafés.',
    tennisTitle: 'Tennis & Squash',
    tennisDesc: 'Une marche de 14 minutes vous mènera au Club de Tennis & Squash avec plusieurs courts éclairés, où les visiteurs peuvent participer à des parties libres. Il y a également des douches et des vestiaires.',
    linkFishing: 'Pêche',
    linkGoKarting: 'Karting',
    linkKayaking: 'Kayak & Canoë',
    linkVeloscenic: 'Parcours Vélo Véloscénic',
    linkTrailMap: 'Carte du Sentier',
    evreuxTitle: 'Évreux',
    evreuxDesc: 'Évreux possède une belle église, un centre historique et des restaurants gastronomiques. Promenez-vous le long des canaux bordés d\'arbres et arrêtez-vous dans un restaurant pour goûter la spécialité locale — le camembert rôti !',
    evreux30min: '30 minutes en voiture',
    versaillesTitle: 'Versailles',
    versaillesDesc: 'Versailles est l\'une des villes les plus visitées au monde grâce à ses kilomètres de forêts préservées, son château royal et son histoire.',
    versailles60min: '60 min en voiture ou 45 min en train',
    verneuiltTitle: 'Verneuil-sur-Avre',
    verneuilDesc: 'Ville médiévale avec château, église et tour. Rues pavées, maisons à colombages, et plein de restaurants et boutiques à explorer.',
    verneuil20min: '20 minutes en voiture',
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  const t = (key) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
